import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import SelectiveAccountStatus from '@/components/auth/SelectiveAccountStatus.jsx';
import LanguageSelector from '@/components/i18n/LanguageSelector.jsx';

function MobileNav({ id, items, activeHref, isOpen, onNavigate, accountMenuProps }) {
    if (!items?.length) {
        return null;
    }

    const {
        user,
        t,
        displayName,
        avatarContainerClasses,
        avatarContent,
        onSignOut,
        isSigningOut,
        onNavigateToSignup,
        onNavigateToLogin,
        onSelectAccountPrivacy,
        showSelectiveAccountStatus = false,
    } = accountMenuProps || {};

    const handleAction = (action) => () => {
        onNavigate?.();
        action?.();
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div
            id={id}
            className="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-y-contain xl:hidden"
        >
            <div className="space-y-3 px-2 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-2 sm:px-3">
                <div className="flex min-h-11 items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                    <span className="text-sm font-medium text-white/75">
                        {t?.('common.language_selector')}
                    </span>
                    <LanguageSelector />
                </div>
                <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    {user ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className={avatarContainerClasses}>{avatarContent}</div>
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-white">
                                        {displayName || t?.('auth.account')}
                                    </p>
                                    <p className="text-xs text-white/60">
                                        {t?.('auth.account')}
                                    </p>
                                </div>
                            </div>
                            {showSelectiveAccountStatus ? (
                                <SelectiveAccountStatus user={user} />
                            ) : null}
                            <Link
                                to="/account/privacy"
                                onClick={handleAction(onSelectAccountPrivacy)}
                                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-white/10 px-3 py-2 text-sm text-white/80 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                            >
                                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                                <span>{t?.('account_privacy.title')}</span>
                            </Link>
                            <button
                                type="button"
                                onClick={handleAction(onSignOut)}
                                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md border border-red-400/20 px-3 py-2 text-sm text-red-300 transition hover:bg-red-400/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                                disabled={isSigningOut}
                            >
                                {isSigningOut ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <LogOut className="h-4 w-4" />
                                )}
                                <span>{t?.('auth.logout')}</span>
                            </button>
                        </div>
                    ) : (
                        <div className="grid gap-2 sm:grid-cols-2">
                            <button
                                type="button"
                                onClick={handleAction(onNavigateToSignup)}
                                className={cn(
                                    buttonVariants({ variant: 'default', size: 'sm' }),
                                    'min-h-11 w-full bg-[var(--primary)] text-white hover:bg-[var(--primary)]/80'
                                )}
                            >
                                {t?.('auth.sign_up')}
                            </button>
                            <button
                                type="button"
                                onClick={handleAction(onNavigateToLogin)}
                                className={cn(
                                    buttonVariants({ variant: 'outline', size: 'sm' }),
                                    'min-h-11 w-full border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/20'
                                )}
                            >
                                {t?.('auth.login')}
                            </button>
                        </div>
                    )}
                </div>
                {items.map((item) => (
                    <Link
                        key={item.id || item.href}
                        to={item.href}
                        aria-current={activeHref === item.href ? 'page' : undefined}
                        className={`flex min-h-11 items-center px-3 py-2 rounded-md text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                            activeHref === item.href
                                ? 'bg-[var(--primary)]/30 text-white'
                                : 'text-gray-300 hover:bg-white/10'
                        }`}
                        onClick={onNavigate}
                    >
                        <item.icon className="w-5 h-5 mr-3" aria-hidden="true" />
                        {item.name}
                    </Link>
                ))}
            </div>
        </div>
    );
}

export default MobileNav;
